import { useEffect, useState } from 'react';
import { collection, getDocs, doc, getDoc, query, where } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * useCohort — loads all cohorts accessible to the current user
 *
 * For beneficiary/coach: derives cohort from their company's cohortId
 * For team/client: loads all cohorts across their programIds
 *
 * Returns:
 *   cohorts       — array of all accessible cohorts
 *   selectedCohort — the currently selected cohort object
 *   setSelectedCohort — setter for cohort switcher
 *   loading       — boolean
 */
export default function useCohort(profile) {
  const [cohorts, setCohorts]               = useState([]);
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [loading, setLoading]               = useState(true);

  useEffect(() => {
    if (!profile?.uid) return;
    async function load() {
      try {
        if (profile.role === 'beneficiary' || profile.role === 'coach') {
          // Load cohort from their company
          const field = profile.role === 'beneficiary' ? 'memberUids' : 'coachId';
          const op    = profile.role === 'beneficiary' ? 'array-contains' : '==';
          const cSnap = await getDocs(query(collection(db, 'companies'), where(field, op, profile.uid)));
          if (!cSnap.empty) {
            const cohortId = cSnap.docs[0].data().cohortId;
            if (cohortId) {
              const cohortDoc = await getDoc(doc(db, 'cohorts', cohortId));
              if (cohortDoc.exists()) {
                const c = { id: cohortDoc.id, ...cohortDoc.data() };
                setCohorts([c]);
                setSelectedCohort(c);
              }
            }
          }
        } else {
          // Team or client — load all cohorts across programIds
          const programIds = profile.programIds || [];
          if (programIds.length === 0) { setLoading(false); return; }

          const allCohorts = [];
          for (const programId of programIds) {
            const snap = await getDocs(
              query(collection(db, 'cohorts'), where('programId', '==', programId))
            );
            snap.docs.forEach(d => {
              if (!allCohorts.find(c => c.id === d.id)) {
                allCohorts.push({ id: d.id, ...d.data() });
              }
            });
          }

          // Sort by most recent first
          allCohorts.sort((a, b) => {
            const da = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
            const db2 = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
            return db2 - da;
          });

          setCohorts(allCohorts);
          setSelectedCohort(allCohorts[0] || null);
        }
      } catch (err) { console.error('useCohort error:', err); }
      finally { setLoading(false); }
    }
    load();
  }, [profile?.uid, profile?.role, profile?.programIds]);

  return { cohorts, selectedCohort, setSelectedCohort, loading };
}
